// Main Stage component factory (refactored to use utilities and subcomponents)
import type {
  AppContext,
  Sketch,
  SketchStroke,
  SketchSummary,
} from "../../types/index.js";
import type {
  StageAPI,
  StageConfig,
  Point,
  TransformState,
  SketchPointerEvent,
  SketchPointerPhase,
  SketchPointerType,
  SketchStrokeRenderInput,
  SketchToolStyle,
  SketchToolType,
} from "../../types/stage-types.js";
import type { AppMode } from "../../utils/layer-manager.js";
import { createPencilDetector } from "../../utils/pencil-detector.js";
import { isIPad } from "../../utils/device.js";
import {
  applySketchPatch,
  createSketch,
  getMe,
  getSketch,
} from "../../api";
import type { SketchOperation } from "unnamed-contracts/dist/models/sketch";
import { SketchTransformUtils } from "unnamed-contracts";
import {
  createTransformManager,
  createCoordinateConverter,
} from "../../utils/transform.js";
import {
  createLayerManager,
  createModeManager,
} from "../../utils/layer-manager.js";

import { createHTMLRenderLayer } from "./subcomponents/HTMLRenderLayer/HTMLRenderLayer.js";
import {
  createCanvasLayer,
  DEFAULT_BALLPOINT_COLOR,
  DEFAULT_BALLPOINT_WIDTH,
} from "./subcomponents/CanvasLayer/CanvasLayer.js";
import type { SketchStrokeModel } from "./subcomponents/CanvasLayer/sketch/ToolManager.js";
import { createUIHelpersLayer } from "./subcomponents/UIHelpersLayer/UIHelpersLayer.js";
import { createUIControlsLayer } from "./subcomponents/UIControlsLayer/UIControlsLayer.js";
import { createUIDedicatedLayer } from "./subcomponents/UIDedicatedLayer/index.js";
import { resolveVisibilityProfile } from "./subcomponents/UIControlsLayer/visibility-profiles";
import { logger } from "../../utils/logger.ts";

const SKETCH_PALETTE_COLORS = [
  { id: "black", label: "Black", color: DEFAULT_BALLPOINT_COLOR },
  { id: "blue", label: "Blue", color: "#2563eb" },
  { id: "red", label: "Red", color: "#ef4444" },
  { id: "green", label: "Green", color: "#16a34a" },
] as const;

const SKETCH_PALETTE_WIDTHS = [
  { id: "thin", label: "2 px", value: 2 },
  { id: "medium", label: "4 px", value: 4 },
  { id: "thick", label: "8 px", value: 8 },
] as const;

const HIGHLIGHTER_OPACITY = 0.35;
const DEFAULT_HIGHLIGHTER_COLOR = "var(--sketch-highlighter-yellow)";
const DEFAULT_HIGHLIGHTER_WIDTH = 16;

const HIGHLIGHTER_PALETTE_COLORS = [
  { id: "yellow", label: "Yellow", color: "var(--sketch-highlighter-yellow)" },
  { id: "cyan", label: "Cyan", color: "var(--sketch-highlighter-cyan)" },
  { id: "orange", label: "Orange", color: "var(--sketch-highlighter-orange)" },
  { id: "green", label: "Green", color: "var(--sketch-highlighter-green)" },
  { id: "grey-1", label: "Grey 1", color: "var(--sketch-highlighter-grey-1)" },
  { id: "grey-2", label: "Grey 2", color: "var(--sketch-highlighter-grey-2)" },
  { id: "grey-3", label: "Grey 3", color: "var(--sketch-highlighter-grey-3)" },
  { id: "grey-4", label: "Grey 4", color: "var(--sketch-highlighter-grey-4)" },
] as const;

const HIGHLIGHTER_PALETTE_WIDTHS = [
  { id: "soft", label: "8 px", value: 8 },
  { id: "medium", label: "16 px", value: 16 },
  { id: "bold", label: "24 px", value: 24 },
] as const;

const DEFAULT_ERASER_WIDTH = 24;
const DEFAULT_ERASER_COLOR = "rgba(15, 23, 42, 0.6)";
const ERASER_PALETTE_WIDTHS = [
  { id: "small", label: "12 px", value: 12 },
  { id: "medium", label: "24 px", value: 24 },
  { id: "large", label: "36 px", value: 36 },
] as const;

/**
 * Creates the main Stage component for sketch-to-code workflow
 * @param container - Container element for the stage
 * @param appContext - Global app context
 * @param config - Stage configuration options
 * @returns Stage API with layer management and transform controls
 */
export const createStage = (
  container: HTMLElement,
  appContext: AppContext,
  config: StageConfig = {}
): StageAPI => {
  let isDestroyed = false;
  const debugLog = (...args: unknown[]): void => {
    if (!logger.isEnabled("stage", "debug")) return;
    const [first, ...rest] = args;
    const message = typeof first === "string" ? first : "stage";
    const extra = typeof first === "string" ? rest : [first, ...rest];
    logger.debug("stage", message, ...extra);
  };
  const debugWarn = (...args: unknown[]): void => {
    if (!logger.isEnabled("stage", "debug")) return;
    const [first, ...rest] = args;
    const message = typeof first === "string" ? first : "stage";
    const extra = typeof first === "string" ? rest : [first, ...rest];
    logger.warn("stage", message, ...extra);
  };

  // Create stage DOM structure
  const stageElement = document.createElement("div");
  stageElement.className = "stage";
  if (isIPad()) {
    stageElement.classList.add("stage--ipad");
  }
  stageElement.innerHTML = `
  <div class="stage__product-container" data-layer="product">
    <div class="stage__html-render" data-layer="html"></div>
    <div class="stage__canvas-layer" data-layer="canvas"></div>
  </div>
  <div class="stage__ui-helpers" data-layer="helpers"></div>
  <div class="stage__events-layer" data-layer="events"></div>
  <div class="stage__ui-dedicated" data-layer="dedicated"></div>
  <div class="stage__ui-controls" data-layer="controls"></div>
  `;

  container.appendChild(stageElement);

  // Get layer elements (eventsLayer used in setupPanningEvents)
  const productContainer = stageElement.querySelector(
    '[data-layer="product"]'
  ) as HTMLElement;
  const htmlRenderContainer = stageElement.querySelector(
    '[data-layer="html"]'
  ) as HTMLElement;
  const canvasContainer = stageElement.querySelector(
    '[data-layer="canvas"]'
  ) as HTMLElement;
  const uiHelpersContainer = stageElement.querySelector(
    '[data-layer="helpers"]'
  ) as HTMLElement;
  const uiControlsLayer = stageElement.querySelector(
    '[data-layer="controls"]'
  ) as HTMLElement;
  const uiDedicatedContainer = stageElement.querySelector(
    '[data-layer="dedicated"]'
  ) as HTMLElement;

  // Create a mutable reference for the transform manager
  let transformManagerRef: ReturnType<typeof createTransformManager> | null =
    null;

  let htmlRenderLayer: ReturnType<typeof createHTMLRenderLayer> | null = null;
  let canvasLayer: ReturnType<typeof createCanvasLayer> | null = null;
  let uiHelpersLayer: ReturnType<typeof createUIHelpersLayer> | null = null;
  let uiDedicatedLayer: ReturnType<typeof createUIDedicatedLayer> | null = null;
  let uiControlsComponent: ReturnType<typeof createUIControlsLayer> | null =
    null;
  let unsubscribeSketchPointer: (() => void) | undefined;
  // removed: activeVisibilityProfileId (no longer needed)
  let showAllState = { edit: false, sketch: false };
  let lastKnownMode: string | null = null;
  let lastKnownSubtool: string | null = null;
  const sketchToolSettings: Record<
    SketchToolType,
    { color: string; width: number; opacity: number }
  > = {
    ballpoint: {
      color: DEFAULT_BALLPOINT_COLOR,
      width: DEFAULT_BALLPOINT_WIDTH,
      opacity: 1,
    },
    highlighter: {
      color: DEFAULT_HIGHLIGHTER_COLOR,
      width: DEFAULT_HIGHLIGHTER_WIDTH,
      opacity: HIGHLIGHTER_OPACITY,
    },
    eraser: {
      color: DEFAULT_ERASER_COLOR,
      width: DEFAULT_ERASER_WIDTH,
      opacity: 1,
    },
    marquee: {
      color: DEFAULT_BALLPOINT_COLOR,
      width: DEFAULT_BALLPOINT_WIDTH,
      opacity: 1,
    },
  };
  let currentSketchTool: SketchToolType = "ballpoint";
  const sketchPaletteOptionsBySubtool = {
    ballpoint: {
      colors: SKETCH_PALETTE_COLORS,
      widths: SKETCH_PALETTE_WIDTHS,
      initialColor: sketchToolSettings.ballpoint.color,
      initialWidth: sketchToolSettings.ballpoint.width,
      toolLabel: "Ballpoint",
      toolVariant: "ballpoint" as const,
    },
    marker: {
      colors: HIGHLIGHTER_PALETTE_COLORS,
      widths: HIGHLIGHTER_PALETTE_WIDTHS,
      initialColor: sketchToolSettings.highlighter.color,
      initialWidth: sketchToolSettings.highlighter.width,
      toolLabel: "Highlighter",
      toolVariant: "highlighter" as const,
    },
    delete: {
      widths: ERASER_PALETTE_WIDTHS,
      initialWidth: sketchToolSettings.eraser.width,
      toolLabel: "Eraser",
      toolVariant: "eraser" as const,
      showColors: false,
    },
  };

  const resolveSketchTool = (subtool?: string | null): SketchToolType =>
    subtool === "marker"
      ? "highlighter"
      : subtool === "delete" || subtool === "delete-clean"
      ? "eraser"
      : subtool === "marquee" || subtool === "lasso"
      ? "marquee"
      : "ballpoint";

  const resolveEraserMode = (subtool?: string | null): "split" | "clean" =>
    subtool === "delete-clean" ? "clean" : "split";

  const applySketchToolSelection = (subtool?: string | null): void => {
    const tool = resolveSketchTool(subtool);
    currentSketchTool = tool;
    const settings = sketchToolSettings[tool];
    canvasLayer?.setSketchStyle?.({
      tool,
      color: settings.color,
      width: settings.width,
      opacity: settings.opacity,
      ...(tool === "eraser"
        ? { eraserMode: resolveEraserMode(subtool) }
        : {}),
      ...(tool === "marquee"
        ? { selectionMode: subtool === "lasso" ? "lasso" : "marquee" }
        : {}),
    });
  };

  const updateSketchUndoRedoState = (): void => {
    if (!uiControlsComponent) return;
    const isSketch = getEffectiveMode() === "sketch";
    if (!isSketch) {
      uiControlsComponent.setUndoDisabled?.(true);
      uiControlsComponent.setRedoDisabled?.(true);
      return;
    }
    const canUndo = canvasLayer?.canUndoSketchStroke?.() ?? false;
    const canRedo = canvasLayer?.canRedoSketchStroke?.() ?? false;
    uiControlsComponent.setUndoDisabled?.(!canUndo);
    uiControlsComponent.setRedoDisabled?.(!canRedo);
  };

  const performUndo = (): void => {
    if (getEffectiveMode() === "sketch") {
      canvasLayer?.undoSketchStroke?.();
      updateSketchUndoRedoState();
      return;
    }
    const undoAction = config.appStateManager?.uiState?.undo?.();
    if (undoAction) updateSketchUndoRedoState();
  };

  const performRedo = (): void => {
    if (getEffectiveMode() === "sketch") {
      canvasLayer?.redoSketchStroke?.();
      updateSketchUndoRedoState();
      return;
    }
    const redoAction = config.appStateManager?.uiState?.redo?.();
    if (redoAction) updateSketchUndoRedoState();
  };

  const confirmDeleteAllSketchStrokes = (): void => {
    if (!activeSketchStrokes.size) return;
    if (!window.confirm("Delete all strokes?")) return;
    const strokeIds = [...activeSketchStrokes.keys()];
    canvasLayer?.removeSketchStrokes?.(strokeIds);
    activeSketchStrokes.clear();
    updateSketchUndoRedoState();
  };

  const selectAllSketchStrokes = (): void => {
    canvasLayer?.selectAllSketchStrokes?.();
  };
  let activeProjectId: string | null = null;
  let activeSketchId: string | null = null;
  let activeSketchVersion: number | null = null;
  let activeSketchStrokes = new Map<string, SketchStroke>();
  let isSavingStrokes = false;
  const hydratedSketchIds = new Set<string>();
  let isResolvingSketchConflict = false;
  let conflictRetryTimer: ReturnType<typeof setTimeout> | null = null;
  const pendingSketchOps: SketchOperation[] = [];
  let unsubscribeProjectChanged: (() => void) | null = null;
  let unsubscribeSketchStrokesPatched: (() => void) | null = null;
  let unsubscribeSketchPatched: (() => void) | null = null;
  let unsubscribeSketchChanged: (() => void) | null = null;
  let unsubscribeSketchCreateRequested: (() => void) | null = null;
  let unsubscribeUndoRequested: (() => void) | null = null;
  let unsubscribeRedoRequested: (() => void) | null = null;
  let sketchCreatePromise: Promise<boolean> | null = null;
  let sketchLoadPromise: Promise<void> | null = null;
  let cachedUserId: string | null = null;
  let userIdPromise: Promise<string | null> | null = null;
  let flushStrokeTimer: number | null = null;

  type StrokeOperation = Extract<
    SketchOperation,
    { op: "stroke.add" | "stroke.replace" | "stroke.remove" }
  >;

  type SketchTransform = Extract<
    SketchOperation,
    { op: "stroke.transform" }
  >["transform"];

  type SketchStrokesPatchedPayload = {
    readonly projectId: string;
    readonly sketchId: string;
    readonly version: number;
    readonly operations: StrokeOperation[];
    readonly updatedAt?: string;
    readonly lastModifiedBy?: string;
    readonly sourceId?: string;
  };

  type SketchPatchedPayload = {
    readonly projectId: string;
    readonly sketchId: string;
    readonly version: number;
    readonly ops: SketchOperation[];
    readonly updatedAt?: string;
    readonly lastModifiedBy?: string;
    readonly sourceId?: string;
  };

  const pencilDetector = createPencilDetector({
    onPencilDetected: (data) => {
      debugLog("🖊️ Apple Pencil detected:", {
        pressure: data.pressure.toFixed(3),
        tiltX: data.tiltX.toFixed(1),
        tiltY: data.tiltY.toFixed(1),
        twist: data.twist.toFixed(1),
      });
      // You can emit events or update UI state here
      // appContext.eventBus.emit('pencil-detected', data);
    },
    onFingerDetected: (data) => {
      debugLog("👆 Finger touch detected:", {
        pressure: data.pressure.toFixed(3),
        contactArea: `${data.width}x${data.height}`,
      });
    },
    onPencilChange: (isPencil) => {
      debugLog(
        `🔄 Input changed to: ${isPencil ? "Apple Pencil" : "finger/mouse"}`
      );
      // Update UI state with pencil connection status
      config.appStateManager?.uiState?.setPencilConnected(isPencil);
    },
    debug: !!appContext.config.debug,
  });

  const applyVisibilityProfile = (
    mode: string | null | undefined,
    overrides: { edit: boolean; sketch: boolean } = showAllState
  ): void => {
    if (
      !htmlRenderLayer ||
      !canvasLayer ||
      !uiHelpersLayer ||
      !uiControlsComponent
    ) {
      return;
    }

    const profile = resolveVisibilityProfile(mode ?? null, {
      showAll: overrides,
    });

    uiDedicatedLayer?.updateContext({
      mode: mode ?? null,
      showAll: overrides,
    });

    // Always apply visibility profile, even if the id hasn't changed.
    // This ensures sub-areas like the sidebar respond to visibility tweaks
    // without requiring a profile id change.

    htmlRenderLayer?.setVisible?.(profile.layers.htmlRender);
    canvasLayer?.setVisible?.(profile.layers.canvas);
    uiHelpersLayer?.setVisible?.(profile.layers.helpers);
    uiDedicatedLayer?.setVisible?.(profile.layers.dedicated);
    uiControlsComponent?.applyVisibilityProfile(profile);

    syncCanvasLayerMode(mode ?? null);
  };

  // Initialize layer components first to get references
  htmlRenderLayer = createHTMLRenderLayer(htmlRenderContainer, {
    enableTestWebsite: true,
    getCurrentScale: () => transformManagerRef?.getTransform().scale || 1,
    appContext,
    uiState: config.appStateManager?.uiState,
  });

  /**
   * Get initial transform from saved state or config
   */
  const getInitialTransform = (): Partial<TransformState> => {
    debugLog("🔍 Stage: Getting initial transform...");
    debugLog("🔍 config.appStateManager:", !!config.appStateManager);
    debugLog(
      "🔍 config.appStateManager?.uiState:",
      !!config.appStateManager?.uiState
    );

    // First try config override
    if (config.initialTransform) {
      debugLog(
        "🎯 Stage: Using config.initialTransform:",
        config.initialTransform
      );
      return config.initialTransform;
    }

    // Then try to restore from saved UI state
    if (config.appStateManager?.uiState) {
      const savedState = config.appStateManager.uiState.getState();
      debugLog("🔍 Stage: Retrieved saved state:", {
        zoom_value: savedState.zoom_value,
        stage_position: savedState.stage_position,
      });

      // Only use saved values if they're not defaults (indicating a previous session)
      const hasCustomZoom = savedState.zoom_value !== 1.0;
      const hasCustomPosition =
        savedState.stage_position.x !== 0 || savedState.stage_position.y !== 0;

      debugLog(
        "🔍 Stage: hasCustomZoom:",
        hasCustomZoom,
        "hasCustomPosition:",
        hasCustomPosition
      );

      if (hasCustomZoom || hasCustomPosition) {
        const initialTransform = {
          scale: savedState.zoom_value,
          translateX: savedState.stage_position.x,
          translateY: savedState.stage_position.y,
          rotation: 0,
        };
        debugLog(
          "🎯 Stage: Using saved state as initial transform:",
          initialTransform
        );
        return initialTransform;
      }
    }

    // Default to empty (transform manager will use its defaults)
    debugLog("🎯 Stage: Using default transform (empty)");
    return {};
  };

  // Initialize utilities with dynamic content centering
  const transformManager = createTransformManager(
    getInitialTransform(),
    () => ({
      width: stageElement.clientWidth,
      height: stageElement.clientHeight,
    }),
    () =>
      htmlRenderLayer?.getContentDimensions() ?? {
        width: stageElement.clientWidth,
        height: stageElement.clientHeight,
      }
  );

  // Set the reference for the HTML render layer to use
  transformManagerRef = transformManager;
  const layerManager = createLayerManager();
  const modeManager = createModeManager(config.mode);
  let unsubscribeSessionUpdate: (() => void) | null = null;

  // Track if we're applying external UIState changes to prevent loops
  let applyingExternalState = false;

  // Helper: read current mode from UI state (fallback to mode manager)
  const getEffectiveMode = (): string => {
    const uiMode = config.appStateManager?.uiState?.getState?.()?.selectedMode;
    if (typeof uiMode === "string" && uiMode.length > 0) return uiMode;
    return modeManager.getMode();
  };

  const toEditRun = (mode: string | null | undefined): "edit" | "run" =>
    mode === "run" ? "run" : "edit";

  function isAppMode(mode: string | null | undefined): mode is AppMode {
    return (
      mode === "edit" ||
      mode === "run" ||
      mode === "sketch" ||
      mode === "ds" ||
      mode === "cms"
    );
  }

  function syncCanvasLayerMode(mode: string | null | undefined): void {
    if (!canvasLayer) return;
    const target: AppMode = isAppMode(mode) ? mode : "edit";
    canvasLayer.setMode(target);
  }

  // Connect transform changes to UI State for zoom controls and position persistence
  if (config.appStateManager?.uiState) {
    transformManager.onTransformChange((transform) => {
      // Skip if we're applying external state changes
      if (applyingExternalState) return;

      const uiState = config.appStateManager.uiState;
      const currentUIState = uiState.getState();

      // Update zoom value in UI State (no decimals, as percentage)
      const zoomPercentage = Math.round(transform.scale * 100) / 100; // Keep 2 decimals for internal precision

      // Round position values to avoid unnecessary updates from tiny floating point differences
      const positionX = Math.round(transform.translateX);
      const positionY = Math.round(transform.translateY);

      // Check if zoom or position has changed significantly
      const zoomChanged =
        Math.abs(currentUIState.zoom_value - zoomPercentage) > 0.01;
      const positionChanged =
        Math.abs(currentUIState.stage_position.x - positionX) > 1 ||
        Math.abs(currentUIState.stage_position.y - positionY) > 1;

      if (zoomChanged || positionChanged) {
        logger.trace("stage", "🎯 Stage: Updating UI State", {
          zoom: zoomPercentage,
          position: { x: positionX, y: positionY },
        });

        // Update zoom if changed
        if (zoomChanged) {
          uiState.setZoom(zoomPercentage, "custom");
        }

        // Update position if changed
        if (positionChanged) {
          uiState.setStagePosition(positionX, positionY);
        }

        // Emit change event for UI components to listen to
        appContext.eventBus.emit("stage-transform-changed", {
          zoomValue: zoomPercentage,
          position: { x: positionX, y: positionY },
          transform: transform,
        });
      }
    });

    // Listen for external UIState changes (from socket) and apply to stage
    const handleUIStateChange = (data: { state: any }) => {
      const state = data?.state;
      try {
        if (!state) return;

        const currentTransform = transformManager.getTransform();
        const newZoom = state.zoom_value;
        const newPos = state.stage_position;

        // Check if there are significant changes
        const zoomChanged =
          newZoom && Math.abs(currentTransform.scale - newZoom) > 0.01;
        const positionChanged =
          newPos &&
          (Math.abs(currentTransform.translateX - newPos.x) > 1 ||
            Math.abs(currentTransform.translateY - newPos.y) > 1);

        if (zoomChanged || positionChanged) {
          logger.debug("stage", "🔄 Stage: Applying external state change", {
            zoom: newZoom,
            position: newPos,
          });

          applyingExternalState = true;
          try {
            transformManager.setTransform({
              scale: newZoom || currentTransform.scale,
              translateX: newPos?.x ?? currentTransform.translateX,
              translateY: newPos?.y ?? currentTransform.translateY,
              rotation: currentTransform.rotation,
            });
          } finally {
            applyingExternalState = false;
          }
        }
      } catch (e) {
        logger.warn("stage", "Failed to apply external state change", e);
      }

      const showAllFromState = (state as any)?.show_all;
      if (showAllFromState && typeof showAllFromState === "object") {
        showAllState = {
          edit: !!showAllFromState.edit,
          sketch: !!showAllFromState.sketch,
        };
      }

      const selectedMode = (state as any)?.selectedMode;
      if (typeof selectedMode === "string") {
        applyVisibilityProfile(selectedMode, showAllState);

        // Detect mode transition from UI state (tools menu)
        if (selectedMode !== lastKnownMode) {
          if (selectedMode === "sketch") {
            // Defer reset slightly to avoid races with hydration
            requestAnimationFrame(() => {
              setTimeout(() => {
                uiHelpersLayer?.resetForMode?.("sketch");
                resetViewportToCenter();
                stageElement.style.touchAction = "none";
                stageElement.style.overflow = "hidden";
                debugLog("✅ UIState → Sketch: reset viewport to 1:1 centered");
              }, 0);
            });
          } else if (selectedMode === "edit") {
            // Restore saved transform from UI state/session when switching back to edit
            const saved = config.appStateManager?.uiState?.getState?.();
            if (saved) {
              const savedScale =
                typeof saved.zoom_value === "number" ? saved.zoom_value : 1;
              const savedX = saved.stage_position?.x ?? 0;
              const savedY = saved.stage_position?.y ?? 0;
              productContainer.style.transformOrigin = "center center";
              transformManager.setTransform({
                scale: savedScale,
                translateX: savedX,
                translateY: savedY,
              } as any);
              applyTransform();
              debugLog("✅ UIState → Edit: restored saved transform");
            }
            uiHelpersLayer?.resetForMode?.("edit");
            stageElement.style.touchAction = "";
            stageElement.style.overflow = "";
          }
          lastKnownMode = selectedMode;
        }
      } else {
        applyVisibilityProfile(null, showAllState);
      }

      const selectedSubtool =
        (state as any)?.selectedTool ??
        (state as any)?.selected_subtool ??
        null;
      const normalizedSubtool =
        typeof selectedSubtool === "string" ? selectedSubtool : null;
      if (normalizedSubtool !== lastKnownSubtool) {
        applySketchToolSelection(normalizedSubtool);
        lastKnownSubtool = normalizedSubtool;
      }

      updateSketchUndoRedoState();
    };

    appContext.eventBus.on("ui-state-changed", handleUIStateChange);
    try {
      const current = config.appStateManager?.uiState?.getState?.();
      if (current) {
        handleUIStateChange({ state: current });
      }
    } catch {}
  }

  unsubscribeUndoRequested = appContext.eventBus.on("undo-requested", () => {
    performUndo();
  });
  unsubscribeRedoRequested = appContext.eventBus.on("redo-requested", () => {
    performRedo();
  });

  // Create coordinate converter (use centered transform when available)
  const coordinateConverter = createCoordinateConverter(
    transformManager.getTransformWithCenter || transformManager.getTransform,
    () => stageElement.getBoundingClientRect()
  );

  const buildContractStroke = (
    stroke: SketchStrokeModel,
    userId: string,
    existing?: SketchStroke | null,
    options: { preservePoints?: boolean } = {}
  ): SketchStroke => {
    const simplifyPoints = (
      points: readonly { x: number; y: number; p?: number; t?: number }[],
      minDistance: number
    ) => {
      if (points.length <= 2) return points;
      const result = [points[0]!];
      const minDistanceSq = minDistance * minDistance;
      let lastKept = points[0]!;
      for (let index = 1; index < points.length - 1; index += 1) {
        const current = points[index]!;
        const dx = current.x - lastKept.x;
        const dy = current.y - lastKept.y;
        if (dx * dx + dy * dy >= minDistanceSq) {
          result.push(current);
          lastKept = current;
        }
      }
      result.push(points[points.length - 1]!);
      return result;
    };

    const input = mapPointerTypeToInput(stroke.pointerType) ?? existing?.input;
    const rawPoints = stroke.points.map((point) => ({
      x: point.x,
      y: point.y,
      ...(typeof point.p === "number" ? { p: point.p } : {}),
      ...(typeof point.t === "number" ? { t: point.t } : {}),
    }));
    const minDistance = stroke.tool === "highlighter" ? 1.5 : 1.0;
    const points = (
      options.preservePoints
        ? rawPoints
        : simplifyPoints(rawPoints, minDistance)
    ) as { x: number; y: number; p?: number; t?: number }[];
    const pressureValues = points
      .map((point) => point.p)
      .filter((value): value is number => typeof value === "number");
    const pressureAvg =
      pressureValues.length > 0
        ? pressureValues.reduce((sum, value) => sum + value, 0) /
          pressureValues.length
        : undefined;

    const createdAt =
      typeof existing?.createdAt === "string"
        ? existing.createdAt
        : Number.isFinite(stroke.createdAt)
        ? new Date(stroke.createdAt).toISOString()
        : new Date().toISOString();

    return {
      id: stroke.id,
      userId,
      points,
      color: stroke.color,
      width: stroke.width,
      type: stroke.tool === "highlighter" ? "highlighter" : "ballpoint",
      ...(stroke.splitFromId ? { splitFromId: stroke.splitFromId } : {}),
      ...(input ? { input } : {}),
      ...(typeof pressureAvg === "number" ? { pressureAvg } : {}),
      createdAt,
    };
  };

  const handleStrokeOperation = (
    operation:
      | { op: "stroke.add"; stroke: SketchStrokeModel }
      | { op: "stroke.replace"; stroke: SketchStrokeModel }
      | { op: "stroke.remove"; strokeId: string }
  ): void => {
    if (!activeProjectId || !activeSketchId) {
      debugWarn("Sketch save skipped: missing project or sketch");
      void (async () => {
        const ready = await ensureActiveSketch();
        if (!ready || !activeSketchId) return;
        const userId = resolveUserId();
        if (!userId) return;
        if (operation.op === "stroke.remove") {
          enqueueSketchOperation({
            op: "stroke.remove",
            strokeId: operation.strokeId,
          });
          return;
        }
        const existing = activeSketchStrokes.get(operation.stroke.id) ?? null;
        const contractStroke = buildContractStroke(
          operation.stroke,
          userId,
          existing,
          {
            preservePoints:
              operation.op === "stroke.replace" ||
              Boolean(operation.stroke.splitFromId),
          }
        );
        enqueueSketchOperation({
          op: operation.op,
          stroke: contractStroke,
        });
      })();
      return;
    }
    if (activeSketchVersion === null) {
      debugWarn("Sketch save skipped: missing sketch version");
      return;
    }
    const userId = resolveUserId();
    if (!userId) {
      debugWarn("Sketch save deferred: missing user id");
      void (async () => {
        const resolved = await ensureUserId();
        if (!resolved) return;
        if (operation.op === "stroke.remove") {
          enqueueSketchOperation({
            op: "stroke.remove",
            strokeId: operation.strokeId,
          });
          return;
        }
        const existing = activeSketchStrokes.get(operation.stroke.id) ?? null;
        const contractStroke = buildContractStroke(
          operation.stroke,
          resolved,
          existing,
          {
            preservePoints:
              operation.op === "stroke.replace" ||
              Boolean(operation.stroke.splitFromId),
          }
        );
        enqueueSketchOperation({
          op: operation.op,
          stroke: contractStroke,
        });
      })();
      return;
    }
    if (operation.op === "stroke.remove") {
      enqueueSketchOperation({
        op: "stroke.remove",
        strokeId: operation.strokeId,
      });
      updateSketchUndoRedoState();
      return;
    }
    const existing = activeSketchStrokes.get(operation.stroke.id) ?? null;
    const contractStroke = buildContractStroke(
      operation.stroke,
      userId,
      existing,
      {
        preservePoints:
          operation.op === "stroke.replace" ||
          Boolean(operation.stroke.splitFromId),
      }
    );
    enqueueSketchOperation({
      op: operation.op,
      stroke: contractStroke,
    });
    updateSketchUndoRedoState();
  };

  const handleTransformOperation = (payload: {
    readonly strokeIds: readonly string[];
    readonly transform: SketchTransform;
    readonly updatedStrokes: readonly SketchStrokeModel[];
  }): void => {
    if (!payload.strokeIds.length) return;
    if (!activeProjectId || !activeSketchId) {
      debugWarn("Sketch transform skipped: missing project or sketch");
      return;
    }
    if (activeSketchVersion === null) {
      debugWarn("Sketch transform skipped: missing sketch version");
      return;
    }
    const resolvedUserId = resolveUserId();
    for (const stroke of payload.updatedStrokes) {
      const existing = activeSketchStrokes.get(stroke.id) ?? null;
      const userId = existing?.userId ?? resolvedUserId;
      if (!userId) continue;
      const contractStroke = buildContractStroke(stroke, userId, existing, {
        preservePoints: true,
      });
      activeSketchStrokes.set(stroke.id, contractStroke);
    }
    enqueueSketchOperation({
      op: "stroke.transform",
      strokeIds: payload.strokeIds as string[],
      transform: payload.transform,
    });
    updateSketchUndoRedoState();
  };

  const getSketchModifiers = (): { shift: boolean; alt: boolean } => {
    const state = config.appStateManager?.uiState?.getState?.();
    return {
      shift: Boolean(state?.shift_pressed),
      alt: Boolean(state?.alt_pressed),
    };
  };

  // Initialize remaining layer components
  canvasLayer = createCanvasLayer(canvasContainer, {
    sketch: {
      viewportWidth: 1920,
      viewportHeight: 1080,
      logPointerEvents: true,
      getModifiers: getSketchModifiers,
      onStrokeOperation: handleStrokeOperation,
      onTransformOperation: handleTransformOperation,
      onHistoryChange: () => {
        updateSketchUndoRedoState();
      },
    },
  });

  const resolveUserId = (): string | null => {
    if (cachedUserId) return cachedUserId;
    try {
      const session = (appContext as any)?.session?.getSession?.() ?? null;
      const user = session?.user ?? null;
      if (typeof user?.id === "string") {
        cachedUserId = user.id;
        return cachedUserId;
      }
      if (typeof (user as any)?.userId === "string") {
        cachedUserId = (user as any).userId;
        return cachedUserId;
      }
      if (typeof (user as any)?._id === "string") {
        cachedUserId = (user as any)._id;
        return cachedUserId;
      }
      return null;
    } catch {
      return null;
    }
  };

  const ensureUserId = async (): Promise<string | null> => {
    const existing = resolveUserId();
    if (existing) return existing;
    if (userIdPromise) return userIdPromise;
    userIdPromise = (async () => {
      try {
        const response = await getMe();
        if (response && typeof response.userId === "string") {
          cachedUserId = response.userId;
          return cachedUserId;
        }
        return null;
      } catch (error) {
        debugWarn("Failed to resolve user id", error);
        return null;
      } finally {
        userIdPromise = null;
      }
    })();
    return userIdPromise;
  };

  const mapPointerTypeToInput = (
    pointerType: SketchPointerType
  ): SketchStroke["input"] | undefined => {
    switch (pointerType) {
      case "pen":
        return "pencil";
      case "touch":
        return "finger";
      case "mouse":
        return "mouse";
      default:
        return undefined;
    }
  };

  const mapInputToPointerType = (
    input?: SketchStroke["input"]
  ): SketchPointerType | undefined => {
    switch (input) {
      case "pencil":
        return "pen";
      case "finger":
        return "touch";
      case "mouse":
        return "mouse";
      default:
        return undefined;
    }
  };

  const toRenderInput = (stroke: SketchStroke): SketchStrokeRenderInput => {
    const pointerType = stroke.input
      ? mapInputToPointerType(stroke.input)
      : undefined;
    return {
      id: stroke.id,
      points: stroke.points as unknown as readonly Point[],
      color: stroke.color,
      width: stroke.width,
      tool: stroke.type === "highlighter" ? "highlighter" : "ballpoint",
      ...(stroke.splitFromId ? { splitFromId: stroke.splitFromId } : {}),
      ...(stroke.type === "highlighter"
        ? { opacity: HIGHLIGHTER_OPACITY }
        : {}),
      ...(pointerType ? { pointerType } : {}),
    };
  };

  const applySketchOperation = (
    operation: SketchOperation,
    options: { render: boolean }
  ): void => {
    switch (operation.op) {
      case "stroke.add": {
        activeSketchStrokes.set(operation.stroke.id, operation.stroke);
        if (options.render) {
          canvasLayer?.commitSketchStroke?.(toRenderInput(operation.stroke));
        }
        return;
      }
      case "stroke.replace": {
        activeSketchStrokes.set(operation.stroke.id, operation.stroke);
        if (options.render) {
          canvasLayer?.removeSketchStroke?.(operation.stroke.id);
          canvasLayer?.commitSketchStroke?.(toRenderInput(operation.stroke));
        }
        return;
      }
      case "stroke.transform": {
        const updatedStrokes: SketchStroke[] = [];
        for (const strokeId of operation.strokeIds) {
          const existing = activeSketchStrokes.get(strokeId);
          if (!existing) continue;
          const updated = SketchTransformUtils.applyTransformToStroke(
            existing,
            operation.transform
          );
          activeSketchStrokes.set(strokeId, updated);
          updatedStrokes.push(updated);
        }
        if (options.render && updatedStrokes.length) {
          canvasLayer?.updateSketchStrokes?.(
            updatedStrokes.map((stroke) => toRenderInput(stroke))
          );
        }
        return;
      }
      case "stroke.remove": {
        activeSketchStrokes.delete(operation.strokeId);
        if (options.render) {
          canvasLayer?.removeSketchStroke?.(operation.strokeId);
        }
        return;
      }
      default:
        return;
    }
  };

  const ensureActiveSketch = async (): Promise<boolean> => {
    if (activeSketchId) return true;
    if (!activeProjectId) return false;
    if (sketchCreatePromise) return sketchCreatePromise;
    sketchCreatePromise = (async () => {
      try {
        const response = await createSketch(activeProjectId, {
          title: "Untitled Sketch",
        });
        if (!response?.ok || !response.sketch) {
          debugWarn("Sketch create failed", response?.error);
          return false;
        }
        setActiveSketch(response.sketch as Sketch);
        return true;
      } catch (error) {
        debugWarn("Sketch create failed", error);
        return false;
      } finally {
        sketchCreatePromise = null;
      }
    })();
    return sketchCreatePromise;
  };

  const loadSketchDetail = async (
    projectId: string,
    sketchId: string
  ): Promise<void> => {
    if (sketchLoadPromise) return sketchLoadPromise;
    sketchLoadPromise = (async () => {
      try {
        const response = await getSketch(projectId, sketchId);
        if (!response?.ok || !response.sketch) {
          debugWarn("Sketch load failed", response?.error);
          return;
        }
        hydratedSketchIds.add(response.sketch.id);
        if (
          response.sketch.id === activeSketchId &&
          (isSavingStrokes || pendingSketchOps.length)
        ) {
          if (typeof response.sketch.version === "number") {
            activeSketchVersion = response.sketch.version;
          }
          hydratedSketchIds.add(response.sketch.id);
          return;
        }
        setActiveSketch(response.sketch as Sketch);
      } catch (error) {
        debugWarn("Sketch load failed", error);
      } finally {
        sketchLoadPromise = null;
      }
    })();
    return sketchLoadPromise;
  };

  const refreshSketchVersion = async (
    projectId: string,
    sketchId: string
  ): Promise<void> => {
    if (isResolvingSketchConflict) return;
    isResolvingSketchConflict = true;
    try {
      const response = await getSketch(projectId, sketchId);
      if (!response?.ok || !response.sketch) {
        debugWarn("Sketch refresh failed", response?.error);
        return;
      }
      if (typeof response.sketch.version === "number") {
        activeSketchVersion = response.sketch.version;
      }
      hydratedSketchIds.add(response.sketch.id);
    } catch (error) {
      debugWarn("Sketch refresh failed", error);
    } finally {
      isResolvingSketchConflict = false;
    }
  };

  const flushSketchQueue = async (): Promise<void> => {
    if (isSavingStrokes) return;
    if (!activeProjectId || !activeSketchId) return;
    if (activeSketchVersion === null) return;
    const userId = await ensureUserId();
    if (!userId) {
      debugWarn("Sketch save skipped: missing user id");
      return;
    }

    const MAX_PATCH_BYTES = 80_000;
    const MAX_PATCH_OPS = 50;
    let totalSize = 0;
    const batch: SketchOperation[] = [];
    while (pendingSketchOps.length > 0 && batch.length < MAX_PATCH_OPS) {
      const next = pendingSketchOps[0]!;
      const nextSize = JSON.stringify(next).length;
      if (batch.length > 0 && totalSize + nextSize > MAX_PATCH_BYTES) {
        break;
      }
      pendingSketchOps.shift();
      batch.push(next);
      totalSize += nextSize;
      if (batch.length === 1 && totalSize > MAX_PATCH_BYTES) {
        debugWarn("Sketch op too large to send in a single patch", {
          size: totalSize,
          op: next.op,
        });
        break;
      }
    }
    if (!batch.length) return;

    isSavingStrokes = true;
    let hadConflict = false;
    try {
      const socketId = config.appStateManager?.getSocketId?.() ?? null;
      const response = await applySketchPatch(
        activeProjectId,
        activeSketchId,
        {
          version: activeSketchVersion,
          lastModifiedBy: userId,
          ops: batch,
        },
        socketId ? { socketId } : undefined
      );
      if (!response?.ok) {
        debugWarn("Sketch save rejected", {
          error: response?.error,
          sketchId: activeSketchId,
          sentVersion: activeSketchVersion,
          serverVersion: response?.version,
        });
        for (let index = batch.length - 1; index >= 0; index -= 1) {
          pendingSketchOps.unshift(batch[index]!);
        }
        if (response?.error === "version_conflict") {
          hadConflict = true;
          if (typeof response.version === "number") {
            activeSketchVersion = response.version;
          } else {
            await refreshSketchVersion(activeProjectId, activeSketchId);
          }
          if (!conflictRetryTimer) {
            conflictRetryTimer = setTimeout(() => {
              conflictRetryTimer = null;
              void flushSketchQueue();
            }, 200);
          }
        }
        return;
      }
      if (typeof response?.version === "number") {
        activeSketchVersion = response.version;
      } else {
        activeSketchVersion += 1;
      }
      for (const op of batch) {
        if (op.op === "stroke.transform") continue;
        applySketchOperation(op, { render: false });
      }
    } catch (error) {
      debugWarn("Sketch save failed", error);
      for (let index = batch.length - 1; index >= 0; index -= 1) {
        pendingSketchOps.unshift(batch[index]!);
      }
    } finally {
      isSavingStrokes = false;
      if (!hadConflict && pendingSketchOps.length) {
        void flushSketchQueue();
      }
    }
  };

  const enqueueSketchOperation = (operation: SketchOperation): void => {
    pendingSketchOps.push(operation);
    if (flushStrokeTimer !== null) return;
    flushStrokeTimer = window.setTimeout(() => {
      flushStrokeTimer = null;
      void flushSketchQueue();
    }, 0);
  };

  const initialSketchStyle: SketchToolStyle | null =
    canvasLayer?.getSketchStyle?.() ?? null;
  if (initialSketchStyle) {
    if (typeof initialSketchStyle.color === "string") {
      sketchToolSettings.ballpoint.color = initialSketchStyle.color;
    }
    if (typeof initialSketchStyle.width === "number") {
      sketchToolSettings.ballpoint.width = initialSketchStyle.width;
    }
    if (typeof initialSketchStyle.opacity === "number") {
      sketchToolSettings.ballpoint.opacity = initialSketchStyle.opacity;
    }
    sketchPaletteOptionsBySubtool.ballpoint.initialColor =
      sketchToolSettings.ballpoint.color;
    sketchPaletteOptionsBySubtool.ballpoint.initialWidth =
      sketchToolSettings.ballpoint.width;
  }

  if (canvasLayer) {
    unsubscribeSketchPointer?.();
    unsubscribeSketchPointer = undefined;

    unsubscribeSketchPointer = canvasLayer.onSketchPointerEvent(
      (event: SketchPointerEvent) => {
        if (
          (event.phase === "down" || event.phase === "move") &&
          event.originalEvent instanceof PointerEvent
        ) {
          pencilDetector.analyzePointerEvent(event.originalEvent);
        }

        const logPayload = {
          phase: event.phase,
          pointerType: event.pointerType,
          position: event.position,
          pressure: Number.isFinite(event.pressure)
            ? Number(event.pressure.toFixed(3))
            : event.pressure,
          isPrimary: event.isPrimary,
          pointerId: event.pointerId,
        };

        if (event.phase === "move") {
          logger.debug("sketch", "✏️ Pointer move", logPayload);
        } else {
          logger.info("sketch", "✏️ Pointer event", logPayload);
        }

        appContext.eventBus.emit("sketch:pointer-event", event);
      }
    );

    syncCanvasLayerMode(getEffectiveMode());
  }

  const setActiveSketch = (sketch: Sketch | null | undefined): void => {
    if (
      sketch &&
      sketch.id === activeSketchId &&
      sketch.strokes?.length === 0 &&
      pendingSketchOps.length
    ) {
      if (typeof sketch.version === "number") {
        activeSketchVersion =
          typeof activeSketchVersion === "number"
            ? Math.max(activeSketchVersion, sketch.version)
            : sketch.version;
      }
      return;
    }
    activeSketchId = sketch?.id ?? null;
    if (typeof sketch?.version === "number") {
      activeSketchVersion =
        typeof activeSketchVersion === "number"
          ? Math.max(activeSketchVersion, sketch.version)
          : sketch.version;
    } else {
      activeSketchVersion = null;
    }
    activeSketchStrokes = new Map(
      (sketch?.strokes ?? []).map((stroke) => [stroke.id, stroke])
    );
    pendingSketchOps.length = 0;
    isSavingStrokes = false;
    const renderStrokes = (sketch?.strokes ?? []).map(toRenderInput);
    canvasLayer?.setSketchStrokes?.(renderStrokes);
    if (
      !sketchLoadPromise &&
      activeProjectId &&
      sketch &&
      sketch.strokes?.length === 0 &&
      !pendingSketchOps.length &&
      !isSavingStrokes &&
      !hydratedSketchIds.has(sketch.id) &&
      typeof sketch.version === "number" &&
      sketch.version > 0
    ) {
      void loadSketchDetail(activeProjectId, sketch.id);
    }
  };

  const handleSketchChanged = (payload: {
    projectId?: string;
    sketchId?: string;
    sketch?: Sketch | null;
  }): void => {
    if (!payload) return;
    if (typeof payload.projectId === "string") {
      activeProjectId = payload.projectId;
    }
    if (payload.sketch) {
      if (payload.sketch.id === activeSketchId) return;
      setActiveSketch(payload.sketch);
      return;
    }
    if (payload.sketchId && payload.sketchId !== activeSketchId) {
      if (activeProjectId) {
        void loadSketchDetail(activeProjectId, payload.sketchId);
      }
    }
  };

  const handleSketchCreateRequested = (payload?: {
    projectId?: string;
    title?: string;
  }): void => {
    if (sketchCreatePromise) return;
    const projectId = payload?.projectId ?? activeProjectId;
    if (!projectId) {
      debugWarn("Sketch create skipped: missing project id");
      return;
    }
    sketchCreatePromise = (async () => {
      try {
        const response = await createSketch(projectId, {
          title: payload?.title ?? "Untitled Sketch",
        });
        if (!response?.ok || !response.sketch) {
          debugWarn("Sketch create failed", response?.error);
          return false;
        }
        activeProjectId = projectId;
        setActiveSketch(response.sketch as Sketch);
        appContext.eventBus.emit("sketch-created", {
          projectId,
          sketch: response.sketch,
        });
        appContext.eventBus.emit("sketch-changed", {
          projectId,
          sketchId: response.sketch.id,
          sketchName: response.sketch.title ?? "Untitled Sketch",
          sketch: response.sketch,
        });
        try {
          appContext.eventBus.emit("route:update", {
            path: `/sketch/${projectId}/${response.sketch.id}`,
          });
        } catch {}
        return true;
      } catch (error) {
        debugWarn("Sketch create failed", error);
        return false;
      } finally {
        sketchCreatePromise = null;
      }
    })();
  };

  unsubscribeProjectChanged = appContext.eventBus.on(
    "project-changed",
    (payload: {
      projectId?: string;
      selectedSketch?: Sketch | null;
      selectedSketchId?: string | null;
      sketches?: readonly SketchSummary[];
    }) => {
      activeProjectId = payload?.projectId ?? null;
      if (payload?.selectedSketch) {
        setActiveSketch(payload.selectedSketch);
        return;
      }
      const fallbackSketchId =
        payload?.selectedSketchId ?? payload?.sketches?.[0]?.id ?? null;
      if (activeProjectId && fallbackSketchId) {
        void loadSketchDetail(activeProjectId, fallbackSketchId);
      } else {
        setActiveSketch(null);
      }
    }
  );

  unsubscribeSketchChanged = appContext.eventBus.on(
    "sketch-changed",
    handleSketchChanged
  );

  unsubscribeSketchCreateRequested = appContext.eventBus.on(
    "sketch-create-requested",
    (payload: { projectId?: string; title?: string } | undefined) => {
      handleSketchCreateRequested(payload);
    }
  );

  unsubscribeSketchStrokesPatched = appContext.eventBus.on(
    "sketch:strokes:patched",
    (payload: SketchStrokesPatchedPayload | undefined) => {
      if (!payload) return;
      if (payload.sourceId) {
        const socketId = config.appStateManager?.getSocketId?.();
        const sourceId = config.appStateManager?.getSourceId?.();
        if (payload.sourceId === socketId || payload.sourceId === sourceId) {
          return;
        }
      }
      if (payload.projectId !== activeProjectId) return;
      if (payload.sketchId !== activeSketchId) return;
      if (
        typeof payload.version === "number" &&
        typeof activeSketchVersion === "number" &&
        payload.version <= activeSketchVersion
      ) {
        return;
      }
      debugLog("🧷 [socket] sketch:strokes:patched", {
        sketchId: payload.sketchId,
        version: payload.version,
        operations: Array.isArray(payload.operations)
          ? payload.operations.length
          : 0,
      });
      if (typeof payload.version === "number") {
        activeSketchVersion = payload.version;
      }
      if (Array.isArray(payload.operations)) {
        for (const op of payload.operations) {
          applySketchOperation(op, { render: true });
        }
      }
    }
  );

  unsubscribeSketchPatched = appContext.eventBus.on(
    "sketch:patched",
    (payload: SketchPatchedPayload | undefined) => {
      if (!payload) return;
      if (payload.sourceId) {
        const socketId = config.appStateManager?.getSocketId?.();
        const sourceId = config.appStateManager?.getSourceId?.();
        if (payload.sourceId === socketId || payload.sourceId === sourceId) {
          return;
        }
      }
      if (payload.projectId !== activeProjectId) return;
      if (payload.sketchId !== activeSketchId) return;
      if (
        typeof payload.version === "number" &&
        typeof activeSketchVersion === "number" &&
        payload.version <= activeSketchVersion
      ) {
        return;
      }
      debugLog("🧷 [socket] sketch:patched", {
        sketchId: payload.sketchId,
        version: payload.version,
        operations: Array.isArray(payload.ops) ? payload.ops.length : 0,
      });
      if (typeof payload.version === "number") {
        activeSketchVersion = payload.version;
      }
      if (Array.isArray(payload.ops)) {
        for (const op of payload.ops) {
          applySketchOperation(op, { render: true });
        }
      }
    }
  );
  try {
    const session = (appContext as any)?.session?.getSession?.() ?? null;
    const bootstrap = session?.currentProject?.project ?? null;
    if (bootstrap?.id) {
      activeProjectId = bootstrap.id;
      const selectedSketch = (bootstrap as any)?.selectedSketch ?? null;
      if (selectedSketch) {
        setActiveSketch(selectedSketch);
      } else if ((bootstrap as any)?.selectedSketchId) {
        void loadSketchDetail(
          bootstrap.id,
          (bootstrap as any).selectedSketchId
        );
      } else if (Array.isArray((bootstrap as any)?.sketches)) {
        const first = (bootstrap as any).sketches[0];
        if (first?.id) {
          void loadSketchDetail(bootstrap.id, first.id);
        }
      } else {
        setActiveSketch(null);
      }
    }
  } catch {}

  uiHelpersLayer = createUIHelpersLayer(uiHelpersContainer, {
    gridSpacing: 20,
    gridVisible: false, // Deactivated for now to focus on other helpers
  });

  if (uiDedicatedContainer) {
    uiDedicatedLayer = createUIDedicatedLayer(uiDedicatedContainer, {
      appStateManager: config.appStateManager,
    });
  }

  debugLog("🎭 Stage: Creating UIControlsLayer");
  uiControlsComponent = createUIControlsLayer(uiControlsLayer, {
    onZoomIn: () => {
      // Reset transform-origin to center for button-based zoom
      productContainer.style.transformOrigin = "center center";

      const currentTransform = transformManager.getTransform();
      const newScale = Math.max(
        0.1,
        Math.min(10, currentTransform.scale * 1.2)
      );
      transformManager.setTransform({ ...currentTransform, scale: newScale });
    },
    onZoomOut: () => {
      // Reset transform-origin to center for button-based zoom
      productContainer.style.transformOrigin = "center center";

      const currentTransform = transformManager.getTransform();
      const newScale = Math.max(
        0.1,
        Math.min(10, currentTransform.scale / 1.2)
      );
      transformManager.setTransform({ ...currentTransform, scale: newScale });
    },
    onReset: () => {
      // Reset transform-origin to center
      productContainer.style.transformOrigin = "center center";
      transformManager.resetTransform();
    },
    onToggleMode: () => {
      modeManager.toggleMode();
    },
    onToggleGrid: () => {
      // For now, just trigger a re-render - we'll improve this API later
      uiHelpersLayer?.render();
    },
    onToggleIconModal: config.onToggleIconModal || (() => {}),
    initialMode: toEditRun(config.mode as any) || "edit",
    appStateManager: config.appStateManager,
    handedness: config.appStateManager?.handedness ?? config.handedness ?? null,
    onUndo: () => {
      performUndo();
    },
    onRedo: () => {
      performRedo();
    },
    onSketchDeleteAll: () => {
      confirmDeleteAllSketchStrokes();
    },
    onSketchSelectAll: () => {
      selectAllSketchStrokes();
    },
    onSketchColorChange: (color) => {
      sketchToolSettings[currentSketchTool].color = color;
      const subtoolKey =
        currentSketchTool === "highlighter"
          ? "marker"
          : currentSketchTool === "eraser"
          ? "delete"
          : "ballpoint";
      const paletteOptions = sketchPaletteOptionsBySubtool[subtoolKey];
      if ("initialColor" in paletteOptions) {
        (paletteOptions as { initialColor: string }).initialColor = color;
      }
      canvasLayer?.setSketchStyle?.({ color });
    },
    onSketchWidthChange: (width) => {
      sketchToolSettings[currentSketchTool].width = width;
      const subtoolKey =
        currentSketchTool === "highlighter"
          ? "marker"
          : currentSketchTool === "eraser"
          ? "delete"
          : "ballpoint";
      sketchPaletteOptionsBySubtool[subtoolKey].initialWidth = width;
      canvasLayer?.setSketchStyle?.({ width });
    },
    sketchPaletteOptions: sketchPaletteOptionsBySubtool.ballpoint,
    sketchPaletteOptionsBySubtool,
  });

  const initialSubtool =
    config.appStateManager?.uiState?.getState?.()?.selectedTool ??
    config.appStateManager?.uiState?.getState?.()?.selected_subtool ??
    null;
  applySketchToolSelection(initialSubtool);
  updateSketchUndoRedoState();

  // Register layers with layer manager
  if (htmlRenderLayer) layerManager.addLayer(htmlRenderLayer);
  if (canvasLayer) layerManager.addLayer(canvasLayer);
  if (uiHelpersLayer) layerManager.addLayer(uiHelpersLayer);
  if (uiDedicatedLayer) layerManager.addLayer(uiDedicatedLayer);
  if (uiControlsComponent) layerManager.addLayer(uiControlsComponent);

  const initialShowAll =
    config.appStateManager?.uiState?.getState?.()?.show_all ?? null;
  if (initialShowAll) {
    showAllState = {
      edit: !!initialShowAll.edit,
      sketch: !!initialShowAll.sketch,
    };
  }

  const initialMode =
    config.appStateManager?.uiState?.getState()?.selectedMode ?? null;
  applyVisibilityProfile(initialMode, showAllState);

  /**
   * Check container proximity and toggle shadows
   */
  const checkContainerProximity = (): void => {
    if (isDestroyed) return;

    const productContainer = container.querySelector(
      ".stage__product-container"
    ) as HTMLElement;
    if (!productContainer) return;

    const containerRect = productContainer.getBoundingClientRect();
    const leftEdgeDistance = containerRect.left;
    const rightEdgeDistance = window.innerWidth - containerRect.right;

    const shouldActivateShadows =
      leftEdgeDistance < 110 || rightEdgeDistance < 110;

    // Find all containers that use the common-container mixin
    const containerSelectors = [
      ".sidebar-controls .flow-controls",
      ".sidebar-controls .tools-main-menu",
      ".sidebar-controls .undo-redo-controls",
      ".sidebar-controls .modifiers-controls",
      ".sidebar-controls .tools-controls .tools-sub-menu .tools-sub-menu__column",
      ".dominant .llm-controls",
      /*  ".dominant .minimap", */
      ".dominant .settings-controls",
    ];

    containerSelectors.forEach((selector) => {
      const element = container.querySelector(selector) as HTMLElement;
      if (element) {
        if (shouldActivateShadows) {
          element.classList.add("container--active");
        } else {
          element.classList.remove("container--active");
        }
      }
    });

    logger.trace("stage", `🔍 Container proximity check`, {
      left: leftEdgeDistance.toFixed(1),
      right: rightEdgeDistance.toFixed(1),
      shadows: shouldActivateShadows,
    });
  };

  /**
   * Apply transform to DOM and notify layers
   */
  const applyTransform = (): void => {
    if (isDestroyed) return;

    const activeMode = getEffectiveMode();

    if (activeMode === "sketch") {
      productContainer.style.transform =
        "translate(0px, 0px) scale(1) rotate(0deg)";
    } else {
      // Apply CSS transform to product container (includes dynamic centering)
      productContainer.style.transform = transformManager.toCSSTransform();
    }

    // Note: transform-origin is set dynamically during zoom operations
    // We don't set CSS custom properties - we only transform the product container directly

    // Check container proximity and toggle shadows
    checkContainerProximity();

    // Notify all managed layers
    layerManager.notifyTransformChange(transformManager.getTransform());

    // Update UI helpers with stage state (they're outside product container now)
    uiHelpersLayer?.updateStageState(
      transformManager.getTransformWithCenter(),
      stageElement.getBoundingClientRect()
    );
  };

  /**
   * Trigger re-centering when content dimensions change
   */
  const recenter = (): void => {
    // Force recalculation of base center offset
    transformManager.recalculateCenter();
    debugLog("🔄 Recentered stage");
    applyTransform();
  };

  /**
   * Reset viewport to centered 1:1 zoom (fit baseline)
   */
  const resetViewportToCenter = (): void => {
    // Ensure transforms apply around center
    productContainer.style.transformOrigin = "center center";

    // Reset transform to identity (scale 1, no translation)
    transformManager.setTransform({
      scale: 1,
      translateX: 0,
      translateY: 0,
      rotation: 0,
    });

    // Recompute base centering from current stage/content and apply
    recenter();
  };

  /**
   * Apply mode changes to DOM
   */
  const applyMode = (): void => {
    if (isDestroyed) return;

    const mode = modeManager.getMode();
    // Apply mode to stage element
    modeManager.applyModeToElement(stageElement);

    // Update UI controls mode indicator
    uiControlsComponent?.updateModeIndicator(
      toEditRun(getEffectiveMode() as any)
    );

    syncCanvasLayerMode(mode);
  };

  // (removed unused restoreSavedState helper)

  /**
   * Initialize the stage
   */
  const init = (): void => {
    // Set initial transform-origin to center
    productContainer.style.transformOrigin = "center center";

    // iPad touch events are setup within setupPanningEvents

    // Setup event listeners
    setupEventListeners();

    // CRITICAL: Force initial centering calculation before applying transforms
    // This ensures saved state is applied with correct centering offset
    transformManager.recalculateCenter();

    // Apply initial state (transform manager already has saved state from getInitialTransform)
    applyTransform();
    applyMode();

    // Set pointer-event policy by mode (disable native touch-action/scroll in sketch)
    const currentMode = getEffectiveMode();
    const eventsLayer = stageElement.querySelector(
      ".stage__events-layer"
    ) as HTMLElement | null;
    if (currentMode === "sketch") {
      stageElement.style.touchAction = "none"; // block browser gestures
      stageElement.style.overflow = "hidden";
      if (eventsLayer) {
        eventsLayer.style.touchAction = "none";
        eventsLayer.style.pointerEvents = "none";
      }
    } else {
      stageElement.style.touchAction = "";
      stageElement.style.overflow = "";
      if (eventsLayer) {
        eventsLayer.style.touchAction = "";
        eventsLayer.style.pointerEvents = "auto";
      }
    }

    // Set up observer for content dimension changes
    setTimeout(() => {
      // Re-center after content has loaded (in case content size changed)
      recenter();

      // Initial container proximity check after UI is loaded
      checkContainerProximity();
    }, 100);
  };

  /**
   * Setup event listeners for utilities and UI interactions
   */
  const setupEventListeners = (): void => {
    // Listen for transform changes
    transformManager.onTransformChange((transform) => {
      applyTransform();

      // Emit to global event bus
      appContext.eventBus.emit("stage-transform-change", {
        transform,
        mode: modeManager.getMode(),
      });

      if (appContext.config.debug) {
        //debugLog("🔄 Stage transform updated:", transform);
      }
    });

    // Listen for mode changes
    modeManager.onModeChange((mode) => {
      applyMode();

      // Reset zoom and position when entering sketch mode (V1: fixed viewport)
      const eventsLayer = stageElement.querySelector(
        ".stage__events-layer"
      ) as HTMLElement | null;
      if (mode === "sketch") {
        debugLog("🎨 ENTERING SKETCH MODE - Disabling zoom/pan");
        uiHelpersLayer?.resetForMode?.("sketch");
        resetViewportToCenter();

        // Disable browser gestures/scroll
        stageElement.style.touchAction = "none";
        stageElement.style.overflow = "hidden";
        if (eventsLayer) {
          eventsLayer.style.touchAction = "none";
          eventsLayer.style.pointerEvents = "none";
        }

        logger.info("stage", "🎨 Sketch mode: Reset to 100% zoom, centered");
      } else if (mode === "edit") {
        // Re-apply saved transform from UI state/session when returning to edit
        const saved = config.appStateManager?.uiState?.getState?.();
        if (saved) {
          const savedScale =
            typeof saved.zoom_value === "number" ? saved.zoom_value : 1;
          const savedX = saved.stage_position?.x ?? 0;
          const savedY = saved.stage_position?.y ?? 0;
          productContainer.style.transformOrigin = "center center";
          transformManager.setTransform({
            scale: savedScale,
            translateX: savedX,
            translateY: savedY,
          } as any);
        }
        uiHelpersLayer?.resetForMode?.("edit");
        // Re-enable browser gestures/scroll
        stageElement.style.touchAction = "";
        stageElement.style.overflow = "";
        if (eventsLayer) {
          eventsLayer.style.touchAction = "";
          eventsLayer.style.pointerEvents = "auto";
        }
        debugLog(
          `🔄 Mode changed to: ${mode} - Zoom/pan ENABLED (restored if available)`
        );
      } else {
        const editRun = toEditRun(mode as any);
        debugLog(
          `🔄 Mode changed to: ${mode} - Zoom/pan ${
            editRun === "edit" ? "ENABLED" : "DISABLED"
          }`
        );
      }

      // Emit to global event bus
      appContext.eventBus.emit("stage-mode-change", {
        mode,
        transform: transformManager.getTransform(),
      });

      if (appContext.config.debug) {
        debugLog(`🎭 Stage mode changed to: ${mode}`);
      }
    });

    // Setup panning interactions
    setupPanningEvents();

    // React to session hydration (e.g., after /session/current-project)
    if (!unsubscribeSessionUpdate) {
      const handleSessionUpdated = (payload: any) => {
        try {
          const session = payload?.session;
          const screen = session?.currentProject?.screenState;
          if (!screen) return;

          // Avoid overriding viewport in sketch mode; only apply incoming transforms in edit
          if (getEffectiveMode() !== "edit") {
            return;
          }

          const current = transformManager.getTransform();
          const next = { ...current } as ReturnType<
            typeof transformManager.getTransform
          >;

          let shouldApply = false;
          if (typeof screen.zoomValue === "number") {
            if (Math.abs(screen.zoomValue - current.scale) > 0.001) {
              (next as any).scale = screen.zoomValue;
              shouldApply = true;
            }
          }
          if (screen.stagePosition) {
            const nx = screen.stagePosition.x;
            const ny = screen.stagePosition.y;
            if (
              Math.abs((current as any).translateX - nx) > 0.5 ||
              Math.abs((current as any).translateY - ny) > 0.5
            ) {
              (next as any).translateX = nx;
              (next as any).translateY = ny;
              shouldApply = true;
            }
          }

          if (shouldApply) {
            // Center origin for programmatic updates to avoid jumps
            const productContainer = stageElement.querySelector(
              '[data-layer="product"]'
            ) as HTMLElement | null;
            if (productContainer)
              productContainer.style.transformOrigin = "center center";
            transformManager.setTransform(next as any);
          }

          const showAllFromScreen =
            (screen as any)?.showAll ?? (screen as any)?.show_all;
          if (showAllFromScreen && typeof showAllFromScreen === "object") {
            showAllState = {
              edit: !!showAllFromScreen.edit,
              sketch: !!showAllFromScreen.sketch,
            };
          }

          const incomingMode = (screen as any)?.selectedMode;
          if (typeof incomingMode === "string") {
            applyVisibilityProfile(incomingMode, showAllState);
          }
        } catch {}
      };

      unsubscribeSessionUpdate = appContext.eventBus.on(
        "session:updated",
        handleSessionUpdated
      );
    }
  };

  /**
   * Setup panning interactions (trackpad scroll + spacebar+drag + iPad touch)
   */
  const setupPanningEvents = (): void => {
    const eventsLayer = stageElement.querySelector(
      ".stage__events-layer"
    ) as HTMLElement;
    if (!eventsLayer) return;

    // Pan interaction state
    let isSpacebarPressed = false;
    let isPanning = false;
    let lastPanPoint: Point | null = null;
    let wheelTimeout: number | null = null;
    let isZooming = false;
    let wheelZoomEndTimeout: number | null = null;
    const activeSketchPointerIds = new Set<number>();
    // Used for touch gesture timing analysis (assigned for debugging/metrics)
    // @ts-expect-error Kept for future use in gesture timing analysis
    let _lastSketchPointerTime = 0;

    // iPad touch state
    // Two-finger double-tap undo stays on the sketch surface to avoid iPad Safari dropouts.
    let lastTouchDistance: number | null = null;
    let initialScale: number = 1;
    let pinchStartTransform: ReturnType<
      typeof transformManager.getTransform
    > | null = null;
    let gestureMode: "none" | "pan" | "pinch" = "none";
    let initialTouchDistance: number | null = null;
    let touchPanPoint: { x: number; y: number } | null = null;
    let suppressSketchTouch = false;
    const pendingTouchDowns = new Map<number, PointerEvent>();
    const pendingTouchTimers = new Map<number, number>();
    const TOUCH_DRAW_DELAY_MS = 80;
    const TWO_FINGER_TAP_MAX_DURATION = 320;
    const TWO_FINGER_TAP_MAX_MOVE = 18;
    const TWO_FINGER_DOUBLE_TAP_WINDOW = 500;
    const TWO_FINGER_DOUBLE_TAP_DISTANCE = 60;
    let twoFingerTapCandidate = false;
    let twoFingerTapStartTime = 0;
    let twoFingerTapStartCenter: Point | null = null;
    let twoFingerTapLastCenter: Point | null = null;
    let twoFingerTapMaxMove = 0;
    let lastTwoFingerTapAt = 0;
    let lastTwoFingerTapCenter: Point | null = null;

    // Grid state tracking - start with grids disabled so first keypress enables them
    let isGridVisible = false; // Start with grid hidden (user expects to enable it)
    let isLinearGridVisible = false; // Start with linear grid hidden (user expects to enable it)
    let isHUDVisible = false; // Start with HUD hidden by default

    /**
     * Update cursor position in HUD
     * @param event - Pointer event with cursor coordinates
     */
    const updateCursorTracking = (event: PointerEvent): void => {
      const rect = eventsLayer.getBoundingClientRect();
      const cursorPosition = {
        x: event.clientX - rect.left,
        y: event.clientY - rect.top,
      };
      uiHelpersLayer?.updateCursorPosition(cursorPosition);
    };

    const dispatchSketchPointer = (
      phase: SketchPointerPhase,
      event: PointerEvent
    ): void => {
      if (!canvasLayer) {
        debugWarn(
          `🚨 SKETCH: dispatchSketchPointer(${phase}) - canvasLayer is null!`
        );
        return;
      }
      debugLog(
        `📤 SKETCH: dispatchSketchPointer(${phase}) pointerId=${event.pointerId} type=${event.pointerType}`
      );
      canvasLayer.dispatchSketchPointerEvent(phase, event);
    };

    /**
     * Zoom at a specific point using transform-origin (elegant approach!)
     * @param screenX - X coordinate in events layer space
     * @param screenY - Y coordinate in events layer space
     * @param zoomFactor - Zoom multiplier (1.1 = 10% zoom in, 0.9 = 10% zoom out)
     */
    const zoomAtPoint = (
      screenX: number,
      screenY: number,
      zoomFactor: number
    ): void => {
      const currentTransform = transformManager.getTransform();

      // Clamp zoom to reasonable limits
      const newScale = Math.max(
        0.1,
        Math.min(10, currentTransform.scale * zoomFactor)
      );
      const actualZoomFactor = newScale / currentTransform.scale;

      if (actualZoomFactor === 1) return; // No zoom change needed

      // ELEGANT APPROACH: Use transform-origin to let the browser handle zoom-to-cursor!

      // Get stage dimensions (the coordinate space we're working in)
      const stageRect = stageElement.getBoundingClientRect();

      // Calculate cursor position as percentages within the stage
      const originX = ((screenX / stageRect.width) * 100).toFixed(2);
      const originY = ((screenY / stageRect.height) * 100).toFixed(2);

      // Set transform-origin to cursor position - browser handles the rest!
      productContainer.style.transformOrigin = `${originX}% ${originY}%`;

      // Apply scale - browser automatically keeps origin point stationary!
      transformManager.setTransform({
        ...currentTransform,
        scale: newScale,
      });

      /*  if (appContext.config.debug) {
        debugLog(
          `🎯 Transform-origin zoom: ${currentTransform.scale.toFixed(
            3
          )} → ${newScale.toFixed(3)}`
        );
        debugLog(
          `📍 Cursor: (${screenX.toFixed(1)}, ${screenY.toFixed(
            1
          )}) → Origin: ${originX}% ${originY}%`
        );
        debugLog(
          `📦 Stage Dimensions: ${stageRect.width.toFixed(
            1
          )} × ${stageRect.height.toFixed(1)} (coordinate space)`
        );
      } */
    };

    /**
     * Calculate distance between two touch points for pinch gestures
     */
    const getTouchDistance = (touch1: Touch, touch2: Touch): number => {
      const dx = touch1.clientX - touch2.clientX;
      const dy = touch1.clientY - touch2.clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const getPointDistance = (a: Point, b: Point): number => {
      const dx = a.x - b.x;
      const dy = a.y - b.y;
      return Math.sqrt(dx * dx + dy * dy);
    };

    /**
     * Get center point between two touches in events layer coordinates
     */
    const getTouchCenter = (touch1: Touch, touch2: Touch): Point => {
      const rect = eventsLayer.getBoundingClientRect();
      return {
        x: (touch1.clientX + touch2.clientX) / 2 - rect.left,
        y: (touch1.clientY + touch2.clientY) / 2 - rect.top,
      };
    };

    /**
     * Filter out touches that are on UI control elements (modifier buttons, etc.)
     */
    const filterStageOnlyTouches = (touches: TouchList): Touch[] => {
      const stageTouches: Touch[] = [];

      for (let i = 0; i < touches.length; i++) {
        const touch = touches[i];
        const element = document.elementFromPoint(
          touch!.clientX,
          touch!.clientY
        );

        // Check if touch is on a UI control element
        const isOnUIControl =
          element?.closest(".modifiers-button") ||
          element?.closest(".sidebar-controls") ||
          element?.closest(".dominant") ||
          element?.closest(".info-hud") ||
          element?.closest(".bottom-area") ||
          element?.closest(".bottom") ||
          element?.closest(".bottom-container");

        // Only include touches that are on the stage area
        if (!isOnUIControl) {
          stageTouches.push(touch!);
        } else {
          debugLog("🎯 Touch filtered out (on UI control):", {
            element: element?.className,
            closest: element?.closest(
              ".modifiers-button, .sidebar-controls, .dominant, .info-hud, .bottom-area"
            )?.className,
          });
        }
      }

      return stageTouches;
    };

    /**
     * Handle iPad touch start events
     */
    const handleTouchStart = (event: TouchEvent): void => {
      // Only allow zoom/pan gestures in edit mode
      const currentMode = getEffectiveMode();
      if (currentMode !== "edit" && currentMode !== "sketch") {
        debugLog(`🚫 Touch start blocked in ${currentMode} mode`);
        return;
      }

      // Analyze for Apple Pencil (fallback method for touch events)
      /* const pencilData =  */ pencilDetector.analyzeTouchEvent(event);

      // Filter to only include touches on the stage area (not UI controls)
      const stageTouches = filterStageOnlyTouches(event.touches);

      debugLog("🎯 Touch filtering:", {
        totalTouches: event.touches.length,
        stageTouches: stageTouches.length,
        filtered: event.touches.length - stageTouches.length,
      });

      if (stageTouches.length === 1) {
        // Single touch - enable highlighting/selection mode (don't prevent default)
        const touch = stageTouches[0]!; // safe: stageTouches.length === 1
        // Store touch position for potential tap detection
        touchPanPoint = { x: touch.clientX, y: touch.clientY };
        suppressSketchTouch = false;
        twoFingerTapCandidate = false;
        // Keep highlighting enabled for single finger
        uiHelpersLayer?.setInteracting(false);
        gestureMode = "none";

        debugLog("📱 iPad: Single touch start (selection mode)");
      } else if (stageTouches.length === 2) {
        // Two touches - prepare for gesture detection
        event.preventDefault();
        const touch1 = stageTouches[0];
        const touch2 = stageTouches[1];

        if (touch1 && touch2) {
          const now =
            typeof performance !== "undefined" ? performance.now() : Date.now();
          const center = getTouchCenter(touch1, touch2);
          twoFingerTapCandidate = true;
          twoFingerTapStartTime = now;
          twoFingerTapStartCenter = center;
          twoFingerTapLastCenter = center;
          twoFingerTapMaxMove = 0;
          suppressSketchTouch = true;
          pendingTouchTimers.forEach((timer) => {
            if (typeof timer === "number") clearTimeout(timer);
          });
          pendingTouchTimers.clear();
          pendingTouchDowns.clear();
          const distance = getTouchDistance(touch1, touch2);
          initialTouchDistance = distance;
          lastTouchDistance = distance;
          initialScale = transformManager.getTransform().scale;
          pinchStartTransform = { ...transformManager.getTransform() };

          // Store center for both pan and pinch
          touchPanPoint = { x: center.x, y: center.y };

          // Set transform origin to touch center for potential pinch
          const stageRect = stageElement.getBoundingClientRect();
          const originX = ((center.x / stageRect.width) * 100).toFixed(2);
          const originY = ((center.y / stageRect.height) * 100).toFixed(2);
          productContainer.style.transformOrigin = `${originX}% ${originY}%`;

          // Disable highlighting during two-finger gestures
          uiHelpersLayer?.setInteracting(true);
          gestureMode = "none"; // Will be determined on first move

          debugLog("📱 iPad: Two-finger gesture start", {
            distance: distance,
            center: center,
          });
        }
      }
    };

    /**
     * Handle iPad touch move events
     */
    const handleTouchMove = (event: TouchEvent): void => {
      const currentMode = getEffectiveMode();
      if (currentMode !== "edit" && currentMode !== "sketch") return;

      // Filter to only include touches on the stage area (not UI controls)
      const stageTouches = filterStageOnlyTouches(event.touches);

      if (stageTouches.length === 1) {
        // Single touch - allow highlighting to work naturally
        // Don't prevent default to allow normal touch behavior for element highlighting
        const touch = stageTouches[0]!; // safe: length checked above
        // Update cursor position for highlighting
        const rect = eventsLayer.getBoundingClientRect();
        const cursorPosition = {
          x: touch.clientX - rect.left,
          y: touch.clientY - rect.top,
        };
        uiHelpersLayer?.updateCursorPosition(cursorPosition);

        debugLog("📱 iPad: Single finger move (highlighting)");
      } else if (stageTouches.length === 2 && touchPanPoint) {
        // Two touches - determine if this is pan or pinch (edit) or tap candidate (sketch)
        event.preventDefault();
        const touch1 = stageTouches[0];
        const touch2 = stageTouches[1];

        if (touch1 && touch2) {
          const center = getTouchCenter(touch1, touch2);
          if (currentMode !== "edit") {
            return;
          }

          if (!initialTouchDistance || !pinchStartTransform) return;
          const currentDistance = getTouchDistance(touch1, touch2);

          // Determine gesture type if not already determined
          if (gestureMode === "none") {
            const distanceChange = Math.abs(
              currentDistance - initialTouchDistance
            );
            const panDistance = Math.sqrt(
              Math.pow(center.x - touchPanPoint.x, 2) +
                Math.pow(center.y - touchPanPoint.y, 2)
            );

            // More sensitive thresholds for better detection
            const pinchThreshold = 15; // pixels - increase for clearer distinction
            const panThreshold = 8; // pixels - lower for more responsive pan

            if (distanceChange > pinchThreshold) {
              gestureMode = "pinch";
              twoFingerTapCandidate = false;
              debugLog("📱 iPad: Gesture detected as PINCH");
            } else if (panDistance > panThreshold) {
              gestureMode = "pan";
              twoFingerTapCandidate = false;
              debugLog("📱 iPad: Gesture detected as PAN");
            }
            // If neither threshold met, stay in 'none' mode
          }

          // Execute gesture based on determined mode
          if (gestureMode === "pinch") {
            if (!isZooming) {
              isZooming = true;
              appContext.eventBus.emit("stage-zoom-start", {});
            }
            // Pinch zoom - calculate scale from initial distance
            const scaleRatio = currentDistance / initialTouchDistance;
            const newScale = Math.max(
              0.1,
              Math.min(10, initialScale * scaleRatio)
            );

            transformManager.setTransform({
              ...pinchStartTransform,
              scale: newScale,
            });

            debugLog(
              `📱 iPad: Pinch zoom scale=${newScale.toFixed(
                3
              )}, ratio=${scaleRatio.toFixed(3)}`
            );
          } else if (gestureMode === "pan") {
            // Pan gesture - move stage based on center movement
            const panDeltaX = center.x - touchPanPoint.x;
            const panDeltaY = center.y - touchPanPoint.y;

            const currentTransform = transformManager.getTransform();
            transformManager.setTransform({
              ...currentTransform,
              translateX: currentTransform.translateX + panDeltaX,
              translateY: currentTransform.translateY + panDeltaY,
            });

            // Update pan point for next frame - this prevents jumping!
            touchPanPoint = { x: center.x, y: center.y };

            debugLog(
              `📱 iPad: Two-finger pan delta(${panDeltaX.toFixed(
                1
              )}, ${panDeltaY.toFixed(1)})`
            );
          }
        }
      }
    };

    /**
     * Handle iPad touch end events
     */
    const handleTouchEnd = (event: TouchEvent): void => {
      const currentMode = getEffectiveMode();
      if (currentMode !== "edit" && currentMode !== "sketch") return;

      const touches = event.touches;
      if (touches.length === 0) {
        suppressSketchTouch = false;
        const now =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        let shouldTriggerUndo = false;

        if (twoFingerTapCandidate && twoFingerTapStartCenter) {
          const duration = now - twoFingerTapStartTime;
          const center = twoFingerTapLastCenter ?? twoFingerTapStartCenter;
          if (
            duration <= TWO_FINGER_TAP_MAX_DURATION &&
            twoFingerTapMaxMove <= TWO_FINGER_TAP_MAX_MOVE
          ) {
            if (
              lastTwoFingerTapAt > 0 &&
              now - lastTwoFingerTapAt <= TWO_FINGER_DOUBLE_TAP_WINDOW &&
              lastTwoFingerTapCenter &&
              getPointDistance(center, lastTwoFingerTapCenter) <=
                TWO_FINGER_DOUBLE_TAP_DISTANCE
            ) {
              shouldTriggerUndo = true;
              lastTwoFingerTapAt = 0;
              lastTwoFingerTapCenter = null;
            } else {
              lastTwoFingerTapAt = now;
              lastTwoFingerTapCenter = center;
            }
          }
        }

        twoFingerTapCandidate = false;
        twoFingerTapStartCenter = null;
        twoFingerTapLastCenter = null;
        twoFingerTapMaxMove = 0;
        // All touches ended
        const wasDoubleTouch = lastTouchDistance !== null;

        if (wasDoubleTouch) {
          // Ending a two-finger gesture
          event.preventDefault();
        } else {
          // Ending a single-finger gesture
          // Don't prevent default to allow normal tap behavior for element selection
          debugLog("📱 iPad: Single finger tap/selection");
        }

        // End any active zoom gesture
        if (isZooming) {
          isZooming = false;
          appContext.eventBus.emit("stage-zoom-end", {});
        }

        // Clean up touch state
        touchPanPoint = null;
        lastTouchDistance = null;
        initialTouchDistance = null;
        pinchStartTransform = null;
        gestureMode = "none";
        // Re-enable highlighting after any gesture
        uiHelpersLayer?.setInteracting(false);

        if (shouldTriggerUndo) {
          appContext.eventBus.emit("undo-requested", { source: "gesture" });
        }

        debugLog("📱 iPad: All touches ended");
      } else if (touches.length === 1 && lastTouchDistance) {
        // Went from two fingers to single finger
        event.preventDefault();
        suppressSketchTouch = false;
        twoFingerTapCandidate = false;
        lastTouchDistance = null;
        initialTouchDistance = null;
        pinchStartTransform = null;
        gestureMode = "none";
        // Transition to single finger highlighting mode
        const touch = touches[0];
        if (touch) {
          touchPanPoint = { x: touch.clientX, y: touch.clientY };

          // Update cursor position for highlighting
          const rect = eventsLayer.getBoundingClientRect();
          const cursorPosition = {
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
          };
          uiHelpersLayer?.updateCursorPosition(cursorPosition);
        }

        // Re-enable highlighting
        uiHelpersLayer?.setInteracting(false);

        debugLog("📱 iPad: Two-finger to single finger (highlighting enabled)");
      }
    };

    // 1. Mouse wheel handling (zoom with modifiers, pan without)
    const handleWheel = (event: WheelEvent): void => {
      // Only interact in edit mode
      const currentMode = getEffectiveMode();
      if (currentMode !== "edit") {
        debugLog(`🚫 Wheel blocked in ${currentMode} mode`);
        return;
      }

      event.preventDefault();

      // Check for zoom modifiers (Ctrl/Cmd + wheel)
      const isZoomModifier = event.ctrlKey || event.metaKey;

      if (isZoomModifier) {
        // ZOOM: Ctrl/Cmd + wheel
        // Disable highlighting during zoom
        uiHelpersLayer?.setInteracting(true);

        // Fire zoom-start on first zoom wheel event
        if (!isZooming) {
          isZooming = true;
          appContext.eventBus.emit("stage-zoom-start", {});
        }

        const zoomDelta = -event.deltaY; // Invert for natural zooming
        const zoomFactor = 1 + zoomDelta * 0.004; // More aggressive zoom speed for better UX

        // Get mouse position for zoom-to-cursor
        const rect = eventsLayer.getBoundingClientRect();
        const mouseX = event.clientX - rect.left;
        const mouseY = event.clientY - rect.top;

        /* if (appContext.config.debug) {
          debugLog(
            `🔍 Wheel zoom: factor=${zoomFactor.toFixed(
              3
            )}, at(${mouseX.toFixed(1)}, ${mouseY.toFixed(1)}) | clientX=${
              event.clientX
            }, rectLeft=${rect.left}`
          );
        } */

        // Apply zoom with cursor focus
        zoomAtPoint(mouseX, mouseY, zoomFactor);

        // Re-enable highlighting after a brief delay
        setTimeout(() => {
          uiHelpersLayer?.setInteracting(false);
        }, 100);

        // Debounce zoom end after wheel zoom bursts
        if (wheelZoomEndTimeout !== null) {
          clearTimeout(wheelZoomEndTimeout);
        }
        wheelZoomEndTimeout = window.setTimeout(() => {
          wheelZoomEndTimeout = null;
          if (isZooming) {
            isZooming = false;
            appContext.eventBus.emit("stage-zoom-end", {});
          }
        }, 150);
      } else {
        // PAN: Regular trackpad scroll
        // Emit pan start on first wheel event
        if (wheelTimeout === null) {
          appContext.eventBus.emit("stage-pan-start", {});
        }

        // Clear existing timeout and set new one
        if (wheelTimeout !== null) {
          clearTimeout(wheelTimeout);
        }
        wheelTimeout = window.setTimeout(() => {
          wheelTimeout = null;
          // Emit pan end after wheel events stop
          appContext.eventBus.emit("stage-pan-end", {});
        }, 150);

        // Disable highlighting during trackpad pan
        uiHelpersLayer?.setInteracting(true);

        const panDeltaX = -event.deltaX;
        const panDeltaY = -event.deltaY;

        // Apply pan to transform
        const currentTransform = transformManager.getTransform();
        transformManager.setTransform({
          ...currentTransform,
          translateX: currentTransform.translateX + panDeltaX,
          translateY: currentTransform.translateY + panDeltaY,
        });

        // Re-enable highlighting after a brief delay
        setTimeout(() => {
          uiHelpersLayer?.setInteracting(false);
        }, 100);

        /* if (appContext.config.debug) {
          debugLog(
            `🔄 Trackpad pan: delta(${panDeltaX.toFixed(
              1
            )}, ${panDeltaY.toFixed(1)})`
          );
        } */
      }
    };

    // 2. Spacebar detection (global)
    const handleKeyDown = (event: KeyboardEvent): void => {
      if (event.code === "Space" && !event.repeat) {
        event.preventDefault();
        isSpacebarPressed = true;
        eventsLayer.style.cursor = "grab";

        if (appContext.config.debug) {
          debugLog("🗝️ Spacebar pressed - pan mode activated");
        }
      }

      // Toggle grid visibility with 'G' key
      if (event.code === "KeyG" && !event.repeat) {
        event.preventDefault();
        // Toggle grid visibility
        isGridVisible = !isGridVisible;
        uiHelpersLayer?.setGridVisible(isGridVisible);

        if (appContext.config.debug) {
          debugLog(`🔢 Grid ${isGridVisible ? "enabled" : "disabled"}`);
        }
      }

      // Toggle linear grid visibility with 'L' key
      if (event.code === "KeyL" && !event.repeat) {
        event.preventDefault();
        // Toggle linear grid visibility
        isLinearGridVisible = !isLinearGridVisible;
        uiHelpersLayer?.setLinearGridVisible(isLinearGridVisible);

        if (appContext.config.debug) {
          debugLog(
            `📏 Linear grid ${isLinearGridVisible ? "enabled" : "disabled"}`
          );
        }
      }

      // Toggle HUD visibility with 'H' key
      if (event.code === "KeyH" && !event.repeat) {
        event.preventDefault();
        // Toggle HUD visibility
        isHUDVisible = !isHUDVisible;
        uiHelpersLayer?.setHUDVisible(isHUDVisible);

        if (appContext.config.debug) {
          debugLog(`📊 HUD ${isHUDVisible ? "enabled" : "disabled"}`);
        }
      }
    };

    const handleKeyUp = (event: KeyboardEvent): void => {
      if (event.code === "Space") {
        event.preventDefault();
        isSpacebarPressed = false;
        isPanning = false;
        lastPanPoint = null;
        eventsLayer.style.cursor = "";

        if (appContext.config.debug) {
          debugLog("🗝️ Spacebar released - pan mode deactivated");
        }
      }
    };

    // 3. Spacebar + drag panning
    const handlePointerDown = (event: PointerEvent): void => {
      const currentMode = getEffectiveMode();

      // Analyze pencil data in modes where pointer interactions are active
      if (currentMode === "edit" || currentMode === "sketch") {
        pencilDetector.analyzePointerEvent(event);
      }

      if (currentMode === "sketch") {
        const clearPendingTouch = (pointerId: number): void => {
          const timer = pendingTouchTimers.get(pointerId);
          if (typeof timer === "number") {
            clearTimeout(timer);
          }
          pendingTouchTimers.delete(pointerId);
          pendingTouchDowns.delete(pointerId);
        };

        if (suppressSketchTouch && event.pointerType === "touch") {
          event.preventDefault();
          return;
        }
        debugLog(
          `📥 SKETCH: handlePointerDown pointerId=${event.pointerId} type=${
            event.pointerType
          } activeIds=[${[...activeSketchPointerIds].join(",")}]`
        );

        // If a new pen stroke starts while there are still active pointers
        // (race condition: up event delayed), properly finish orphaned strokes
        if (event.pointerType === "pen" && activeSketchPointerIds.size > 0) {
          // Don't clear if this is the same pointer ID (browser reuse)
          if (!activeSketchPointerIds.has(event.pointerId)) {
            debugLog(
              `🧹 SKETCH: Cleaning up ${activeSketchPointerIds.size} orphaned pointers`
            );
            // Dispatch "cancel" for orphaned pointers to properly close strokes
            activeSketchPointerIds.forEach((pointerId) => {
              debugLog(
                `🧹 SKETCH: Force-finishing orphaned stroke for pointerId=${pointerId}`
              );
              try {
                eventsLayer.releasePointerCapture(pointerId);
              } catch {
                // Ignore release errors
              }
              // Create a synthetic cancel event to properly close the orphaned stroke
              canvasLayer?.forceFinishStroke?.(pointerId);
            });
            activeSketchPointerIds.clear();
          } else {
            debugLog(
              `⚠️ SKETCH: Same pointer ID ${event.pointerId} reused - skipping cleanup`
            );
          }
        }
        event.preventDefault();
        if (event.pointerType === "touch") {
          pendingTouchDowns.set(event.pointerId, event);
          clearPendingTouch(event.pointerId);
          const timerId = window.setTimeout(() => {
            const pending = pendingTouchDowns.get(event.pointerId);
            if (
              pending &&
              !suppressSketchTouch &&
              !activeSketchPointerIds.has(event.pointerId)
            ) {
              activeSketchPointerIds.add(event.pointerId);
              _lastSketchPointerTime = performance.now?.() ?? Date.now();
              try {
                eventsLayer.setPointerCapture(event.pointerId);
              } catch (e) {
                debugWarn(
                  `❌ SKETCH: Pointer capture FAILED for pointerId=${event.pointerId}`,
                  e
                );
              }
              dispatchSketchPointer("down", pending);
            }
            clearPendingTouch(event.pointerId);
          }, TOUCH_DRAW_DELAY_MS);
          pendingTouchTimers.set(event.pointerId, timerId);
        } else {
          activeSketchPointerIds.add(event.pointerId);
          _lastSketchPointerTime = performance.now?.() ?? Date.now();
          try {
            eventsLayer.setPointerCapture(event.pointerId);
            debugLog(
              `✅ SKETCH: Pointer capture set for pointerId=${event.pointerId}`
            );
          } catch (e) {
            debugWarn(
              `❌ SKETCH: Pointer capture FAILED for pointerId=${event.pointerId}`,
              e
            );
          }
          dispatchSketchPointer("down", event);
        }
        return;
      }

      if (!isSpacebarPressed || currentMode !== "edit") return;

      event.preventDefault();
      isPanning = true;
      lastPanPoint = { x: event.clientX, y: event.clientY };
      eventsLayer.style.cursor = "grabbing";
      eventsLayer.setPointerCapture(event.pointerId);

      // Disable element highlighting during pan operations
      uiHelpersLayer?.setInteracting(true);

      // Emit pan start event to suppress persistence
      appContext.eventBus.emit("stage-pan-start", {});

      if (appContext.config.debug) {
        debugLog("🖱️ Pan drag started");
      }
    };

    const handlePointerMove = (event: PointerEvent): void => {
      // Always update cursor position for HUD
      updateCursorTracking(event);

      const currentMode = getEffectiveMode();
      if (currentMode === "sketch") {
        if (suppressSketchTouch && event.pointerType === "touch") {
          event.preventDefault();
          return;
        }
        if (
          event.pointerType === "touch" &&
          pendingTouchDowns.has(event.pointerId)
        ) {
          event.preventDefault();
          return;
        }
        if (!activeSketchPointerIds.has(event.pointerId)) {
          if (event.pointerType === "pen") {
            event.preventDefault();
            activeSketchPointerIds.add(event.pointerId);
            eventsLayer.setPointerCapture(event.pointerId);
            dispatchSketchPointer("down", event);
            dispatchSketchPointer("move", event);
            _lastSketchPointerTime = performance.now?.() ?? Date.now();
          }
          return;
        }
        event.preventDefault();
        _lastSketchPointerTime = performance.now?.() ?? Date.now();
        dispatchSketchPointer("move", event);
        return;
      }

      if (!isPanning || !lastPanPoint || !isSpacebarPressed) return;

      event.preventDefault();

      // Calculate pan delta
      const panDeltaX = event.clientX - lastPanPoint.x;
      const panDeltaY = event.clientY - lastPanPoint.y;

      // Apply pan to transform
      const currentTransform = transformManager.getTransform();
      transformManager.setTransform({
        ...currentTransform,
        translateX: currentTransform.translateX + panDeltaX,
        translateY: currentTransform.translateY + panDeltaY,
      });

      // Update last pan point
      lastPanPoint = { x: event.clientX, y: event.clientY };

      if (appContext.config.debug) {
        debugLog(
          `🖱️ Drag pan: delta(${panDeltaX.toFixed(1)}, ${panDeltaY.toFixed(1)})`
        );
      }
    };

    const handlePointerUp = (event: PointerEvent): void => {
      if (getEffectiveMode() === "sketch") {
        if (
          event.pointerType === "touch" &&
          pendingTouchDowns.has(event.pointerId)
        ) {
          const timer = pendingTouchTimers.get(event.pointerId);
          if (typeof timer === "number") {
            clearTimeout(timer);
            pendingTouchTimers.delete(event.pointerId);
          }
          pendingTouchDowns.delete(event.pointerId);
          event.preventDefault();
          return;
        }
        if (suppressSketchTouch && event.pointerType === "touch") {
          event.preventDefault();
          return;
        }
        debugLog(
          `📥 SKETCH: handlePointerUp pointerId=${event.pointerId} type=${
            event.pointerType
          } activeIds=[${[...activeSketchPointerIds].join(",")}]`
        );

        if (!activeSketchPointerIds.has(event.pointerId)) {
          // Orphan up event - the pointer was already cleaned up (likely due to
          // rapid stroke succession). Just ignore it - the stroke was already
          // finished via forceFinishStroke. Don't try to reconstruct phantom strokes.
          debugLog(
            `⏭️ SKETCH: Ignoring orphan up event for pointerId=${event.pointerId}`
          );
          event.preventDefault();
          return;
        }

        event.preventDefault();
        activeSketchPointerIds.delete(event.pointerId);
        try {
          eventsLayer.releasePointerCapture(event.pointerId);
        } catch {
          // Ignore release errors
        }
        _lastSketchPointerTime = performance.now?.() ?? Date.now();
        dispatchSketchPointer("up", event);
        return;
      }

      if (!isPanning) return;

      event.preventDefault();
      isPanning = false;
      lastPanPoint = null;
      eventsLayer.style.cursor = isSpacebarPressed ? "grab" : "";
      eventsLayer.releasePointerCapture(event.pointerId);

      // Re-enable element highlighting after pan operations
      uiHelpersLayer?.setInteracting(false);

      // Emit pan end event to trigger persistence
      appContext.eventBus.emit("stage-pan-end", {});

      if (appContext.config.debug) {
        debugLog("🖱️ Pan drag ended");
      }
    };

    const handlePointerCancel = (event: PointerEvent): void => {
      if (getEffectiveMode() === "sketch") {
        if (
          event.pointerType === "touch" &&
          pendingTouchDowns.has(event.pointerId)
        ) {
          const timer = pendingTouchTimers.get(event.pointerId);
          if (typeof timer === "number") {
            clearTimeout(timer);
            pendingTouchTimers.delete(event.pointerId);
          }
          pendingTouchDowns.delete(event.pointerId);
          event.preventDefault();
          return;
        }
        if (suppressSketchTouch && event.pointerType === "touch") {
          event.preventDefault();
          return;
        }
        if (!activeSketchPointerIds.has(event.pointerId)) return;

        event.preventDefault();
        activeSketchPointerIds.delete(event.pointerId);
        eventsLayer.releasePointerCapture(event.pointerId);
        _lastSketchPointerTime = performance.now?.() ?? Date.now();
        dispatchSketchPointer("cancel", event);
        return;
      }

      if (!isPanning) return;

      event.preventDefault();
      isPanning = false;
      lastPanPoint = null;
      eventsLayer.style.cursor = isSpacebarPressed ? "grab" : "";
      eventsLayer.releasePointerCapture(event.pointerId);
      uiHelpersLayer?.setInteracting(false);
      appContext.eventBus.emit("stage-pan-end", {});

      if (appContext.config.debug) {
        debugLog("🖱️ Pan drag cancelled");
      }
    };

    const handlePointerLeave = (): void => {
      // Clear cursor tracking when mouse leaves stage
      uiHelpersLayer?.updateCursorPosition(null);
    };

    // Attach event listeners
    eventsLayer.addEventListener("wheel", handleWheel, { passive: false });
    eventsLayer.addEventListener("pointerdown", handlePointerDown);
    eventsLayer.addEventListener("pointermove", handlePointerMove);
    eventsLayer.addEventListener("pointerup", handlePointerUp);
    eventsLayer.addEventListener("pointercancel", handlePointerCancel);
    eventsLayer.addEventListener("pointerleave", handlePointerLeave);

    // iPad touch events on the stage element
    stageElement.addEventListener("touchstart", handleTouchStart, {
      passive: false,
    });
    stageElement.addEventListener("touchmove", handleTouchMove, {
      passive: false,
    });
    stageElement.addEventListener("touchend", handleTouchEnd, {
      passive: false,
    });
    stageElement.addEventListener("touchcancel", handleTouchEnd, {
      passive: false,
    });

    // Spacebar events need to be global
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("keyup", handleKeyUp);

    // Window resize listener for container proximity checking
    const handleWindowResize = (): void => {
      // Use requestAnimationFrame to debounce resize events
      requestAnimationFrame(() => {
        checkContainerProximity();
      });
    };
    window.addEventListener("resize", handleWindowResize);

    // Cleanup function (we'll need to call this in destroy)
    const cleanupPanning = (): void => {
      eventsLayer.removeEventListener("wheel", handleWheel);
      eventsLayer.removeEventListener("pointerdown", handlePointerDown);
      eventsLayer.removeEventListener("pointermove", handlePointerMove);
      eventsLayer.removeEventListener("pointerup", handlePointerUp);
      eventsLayer.removeEventListener("pointercancel", handlePointerCancel);
      eventsLayer.removeEventListener("pointerleave", handlePointerLeave);

      // Clean up iPad touch events
      stageElement.removeEventListener("touchstart", handleTouchStart);
      stageElement.removeEventListener("touchmove", handleTouchMove);
      stageElement.removeEventListener("touchend", handleTouchEnd);
      stageElement.removeEventListener("touchcancel", handleTouchEnd);

      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keyup", handleKeyUp);
      window.removeEventListener("resize", handleWindowResize);

      activeSketchPointerIds.clear();
    };

    // Store cleanup function and zoom function for later use
    (setupPanningEvents as any).cleanup = cleanupPanning;
    (setupPanningEvents as any).zoomAtPoint = zoomAtPoint;
  };

  /**
   * Cleanup resources
   */
  const destroy = (): void => {
    if (isDestroyed) return;

    isDestroyed = true;

    // Cleanup panning events
    if ((setupPanningEvents as any).cleanup) {
      (setupPanningEvents as any).cleanup();
    }

    // Destroy utilities
    transformManager.destroy();
    layerManager.destroy();
    modeManager.destroy();
    pencilDetector.destroy();

    if (unsubscribeSessionUpdate) {
      unsubscribeSessionUpdate();
      unsubscribeSessionUpdate = null;
    }
    unsubscribeSketchPointer?.();
    unsubscribeSketchPointer = undefined;
    unsubscribeProjectChanged?.();
    unsubscribeProjectChanged = null;
    unsubscribeSketchStrokesPatched?.();
    unsubscribeSketchStrokesPatched = null;
    unsubscribeSketchPatched?.();
    unsubscribeSketchPatched = null;
    if (flushStrokeTimer !== null) {
      clearTimeout(flushStrokeTimer);
      flushStrokeTimer = null;
    }
    unsubscribeSketchChanged?.();
    unsubscribeSketchChanged = null;
    unsubscribeSketchCreateRequested?.();
    unsubscribeSketchCreateRequested = null;
    unsubscribeUndoRequested?.();
    unsubscribeUndoRequested = null;
    unsubscribeRedoRequested?.();
    unsubscribeRedoRequested = null;

    // Remove DOM elements
    if (stageElement.parentNode) {
      stageElement.parentNode.removeChild(stageElement);
    }
  };

  // Initialize stage
  init();

  // Return public API (delegating to utilities)
  return {
    setTransform: transformManager.setTransform,
    getTransform: transformManager.getTransform,
    setMode: modeManager.setMode,
    getMode: modeManager.getMode,
    screenToStage: coordinateConverter.screenToStage,
    stageToScreen: coordinateConverter.stageToScreen,
    recenter,
    destroy,
  };
};
